/**
 * A business-wide insight spec becomes one SQL statement over the whole mirror.
 *
 * The sibling of `insight-query.ts`, and the same discipline: it builds SQL and
 * never runs it, so it tests under bun where expo-sqlite cannot load.
 * Identifiers are literal here — there are no per-record columns to quote —
 * and every value is bound.
 *
 * **Why a second compiler and not a wider first one.** A record spec names a
 * field: its measures, groups and conditions all reach `f_<fieldId>` columns of
 * that record's own view. Those columns exist in no business-wide FROM. Making
 * one compiler serve both would mean runtime guards against specs the type
 * system should have refused. So the grammar below has NO string-typed field
 * slot anywhere: every axis is a closed union, and "a business card can never
 * ask about a field" is a fact the compiler cannot violate. What is genuinely
 * shared — the window arithmetic, the result shapes, the row readers — is
 * imported from `insight-query.ts` rather than copied.
 *
 * **What a business can be asked, and why it is so short.** Two records in one
 * business have different field ids, and one may have no comparable field at
 * all. So the only columns every entry shares are the materialized ones and
 * what the catalog knows about its record. That is the whole vocabulary:
 *
 *   signed_value   money, already signed by the record's own sign binding
 *   day            when it happened
 *   record_id      which record
 *   created_by     who wrote it
 *   source_channel how it arrived
 *   r.kind, r.template_id, r.currency   what sort of record it is
 *
 * "Payment mode across the business" is not in that list and never will be.
 *
 * What DOES ask about a field is a card hosted here: `{ host, record }` carries
 * a record's own spec, compiled by `insight-query.ts` over that record's view.
 * The Dashboard is where such a card lives because a total that reaches two
 * records belongs to neither of them. The vocabulary above is untouched by it —
 * see `HostedRecordSpec`.
 *
 * Four rules are on every statement.
 *
 *   e.tenant_id          WHICH business. One phone can hold several, and none
 *                        of these statements names a record, so without it
 *                        "the whole business" would mean all of them at once
 *   deleted_at IS NULL   a tombstone is not money
 *   r.kind               records and lists are counted separately, never together
 *   the currency rule    a money statement picks one currency and leaves out
 *                        the records carrying another, exactly as `todayTotals`
 *                        does. A count needs no currency and does not apply it,
 *                        which is why a stock-only business still gets cards.
 */
import { INSIGHT_AGG_MAX_LEAVES, type FormulaCalcOp, type VisibleRecordKind } from '@alluzo/shared';

import {
  GROUP_SCAN_LIMIT,
  previousWindowFor,
  seriesWindow,
  windowFor,
  type CompiledInsight,
  type DayWindow,
  type InsightPairScope,
  type InsightScope,
  type InsightShape,
  type InsightSpec,
} from '@/lib/insight-query';

/* ------------------------------------------------------------------- spec */

/**
 * Which side of the money a measure reads.
 *
 *   in    what came in. Zero counts as in, matching `toTodayTotals`.
 *   out   what went out, as a POSITIVE number.
 *   net   in minus out. On a business holding one record that takes money and
 *         one that spends it, this is profit — and no single record can
 *         answer it, which is the whole reason this file exists.
 */
export type BusinessFlow = 'in' | 'out' | 'net';

export const BUSINESS_FLOWS: readonly BusinessFlow[] = ['in', 'out', 'net'];

/**
 * One total a combined card adds up: money of one flow, or a count of entries.
 * What each leaf of a Combine may be, and what a plain card's measure is most
 * of the time.
 */
export type BusinessPlainMeasure =
  | { readonly agg: 'sum' | 'avg' | 'min' | 'max'; readonly flow: BusinessFlow }
  | { readonly agg: 'rows' };

/**
 * One total of a combined workspace card: a plain measure with conditions of
 * its own. "Money in, only Daily sales" minus "Money out, only Shop expenses".
 *
 * The sibling of a record's `InsightAggLeaf`, in the workspace's own closed
 * vocabulary: a leaf names no field, only the flows, the count and the
 * conditions every entry of every record can answer.
 */
export type BusinessAggLeaf = {
  readonly kind: 'agg';
  readonly measure: BusinessPlainMeasure;
  readonly filters?: readonly BusinessFilter[];
};

/**
 * The aggregate tree of a combined workspace card: totals at the leaves,
 * arithmetic above them — the record's `InsightAggNode`, with workspace leaves.
 */
export type BusinessAggNode =
  | BusinessAggLeaf
  | { readonly kind: 'const'; readonly value: number }
  | {
      readonly kind: 'calc';
      readonly op: FormulaCalcOp;
      readonly left: BusinessAggNode;
      readonly right: BusinessAggNode;
    }
  | { readonly kind: 'round'; readonly of: BusinessAggNode; readonly decimals: number };

/**
 * What to compute.
 *
 *   sum/avg/min/max   money, so the currency rule applies
 *   rows              counting entries. No currency, no measure needed.
 *   idle_days         how long since a record was last written to. Grouped by
 *                     record only, and always over all of time: "quiet since
 *                     Tuesday" is not a question about a window.
 *   formula           Combine: totals with their own conditions, joined by
 *                     arithmetic (A − B). Each total is worked out over the
 *                     same window and the card's own conditions.
 */
export type BusinessMeasure =
  | BusinessPlainMeasure
  | { readonly agg: 'idle_days' }
  | { readonly agg: 'formula'; readonly expr: BusinessAggNode };

/** Every total in a combined card's tree, in the order they are written. */
export const businessLeavesOf = (node: BusinessAggNode): readonly BusinessAggLeaf[] => {
  if (node.kind === 'agg') return [node];
  if (node.kind === 'calc') return [...businessLeavesOf(node.left), ...businessLeavesOf(node.right)];
  if (node.kind === 'round') return businessLeavesOf(node.of);
  return [];
};

/** True when a plain measure reads money. */
const plainIsMoney = (measure: BusinessPlainMeasure): boolean => measure.agg !== 'rows';

/**
 * True when the measure reads money, and so needs a currency to be in. A
 * combined card does when any of its totals does.
 */
export const isMoneyMeasure = (measure: BusinessMeasure): boolean => {
  if (measure.agg === 'formula') return businessLeavesOf(measure.expr).some((leaf) => plainIsMoney(leaf.measure));
  return measure.agg !== 'rows' && measure.agg !== 'idle_days';
};

/** What a business-wide breakdown may group by. Never a field. */
export type BusinessGroupKey = 'record' | 'creator' | 'channel' | 'day';

export const BUSINESS_GROUP_KEYS: readonly BusinessGroupKey[] = [
  'record',
  'creator',
  'channel',
  'day',
];

/** What a business-wide condition may name. Never a field. */
export type BusinessFilterKey = 'record' | 'template' | 'creator' | 'channel';

export const BUSINESS_FILTER_KEYS: readonly BusinessFilterKey[] = [
  'record',
  'template',
  'creator',
  'channel',
];

export type BusinessFilterOp = 'is' | 'is_not' | 'in' | 'not_in';

export const BUSINESS_FILTER_OPS: readonly BusinessFilterOp[] = ['is', 'is_not', 'in', 'not_in'];

/**
 * A condition on a business-wide card.
 *
 * `value` is a record id, a template id, a channel word, or a person. A person
 * may be the literal `'me'`, which resolves at compile time to whoever is
 * signed in — so a stored layout stays honest after a different person signs
 * in on the same phone, rather than silently reporting somebody else's work.
 *
 * `in` and `not_in` ("is any of", "is none of") carry a list of those values,
 * and only they do, so a card saved with one value reads as it always has.
 */
export type BusinessFilter =
  | { readonly key: BusinessFilterKey; readonly op: 'is' | 'is_not'; readonly value: string }
  | { readonly key: BusinessFilterKey; readonly op: 'in' | 'not_in'; readonly value: readonly string[] };

/** The two list ops, "is any of" and "is none of". */
export type BusinessListFilter = Extract<BusinessFilter, { readonly op: 'in' | 'not_in' }>;

/** True when the condition carries a list. */
export const isBusinessListFilter = (filter: BusinessFilter): filter is BusinessListFilter =>
  filter.op === 'in' || filter.op === 'not_in';

/** A condition's values as a list, whichever op it has. */
export const businessFilterValues = (filter: BusinessFilter): readonly string[] =>
  isBusinessListFilter(filter) ? filter.value : [filter.value];

/** A condition's value as one string, for keying and ordering. */
export const businessValueKey = (filter: BusinessFilter): string =>
  businessFilterValues(filter).join('\u001f');

/** The literal a creator condition uses for whoever is signed in. */
export const ME = 'me';

/** Which half of the catalog a statement reads. Records by default. */
export type BusinessKind = VisibleRecordKind;

type Scoped = {
  readonly filters?: readonly BusinessFilter[];
  /** Defaults to `'record'`. Only a count of list rows sets `'directory'`. */
  readonly kind?: BusinessKind;
};

export type BusinessScalarSpec = Scoped & {
  readonly shape: 'scalar';
  readonly measure: BusinessMeasure;
  readonly scope: InsightScope;
  /** With it, the answer is the largest group's number and that group's key. */
  readonly leaderOf?: BusinessGroupKey;
};

export type BusinessPairSpec = Scoped & {
  readonly shape: 'scalar_pair';
  readonly measure: BusinessMeasure;
  readonly scope: InsightPairScope;
};

export type BusinessSeriesSpec = Scoped & {
  readonly shape: 'series';
  readonly measure: BusinessMeasure;
  readonly days: number;
};

export type BusinessBreakdownSpec = Scoped & {
  readonly shape: 'breakdown';
  readonly measure: BusinessMeasure;
  readonly by: BusinessGroupKey;
  readonly scope: InsightScope;
  readonly limit: number;
};

/** The four questions a business can be asked in its own closed vocabulary. */
export type BusinessOwnSpec =
  | BusinessScalarSpec
  | BusinessPairSpec
  | BusinessSeriesSpec
  | BusinessBreakdownSpec;

/**
 * A record's own card, hosted on the Dashboard.
 *
 * The fifth member, and the one that names a field. It is NOT a widening of
 * the vocabulary above: the spec inside is a record spec, compiled by
 * `insight-query.ts` against `host`'s own view, with every field id belonging
 * to that record. This file's grammar is untouched, and "a business card can
 * never ask about a field" is still true of it.
 *
 * It lives here because the Dashboard is where a card whose totals reach two
 * records belongs: `v_deliveries` and `v_payments` are both on this device, and
 * neither record's own summary can honestly host a card about the other.
 *
 * `host` is the record whose view the statement runs over — the record that
 * owns the group field and any hop the spec makes.
 */
export type HostedRecordSpec = {
  readonly host: string;
  readonly record: InsightSpec;
};

export type BusinessInsightSpec = BusinessOwnSpec | HostedRecordSpec;

/** True when the card is a record's own question standing on the Dashboard. */
export const isHostedSpec = (spec: BusinessInsightSpec): spec is HostedRecordSpec =>
  'host' in spec;

/** The shape a card draws, whichever half of the union it came from. */
export const businessShapeOf = (spec: BusinessInsightSpec): InsightShape =>
  isHostedSpec(spec) ? spec.record.shape : spec.shape;

/**
 * What the compiler needs beyond the spec.
 *
 * The currency is picked once per screen by `pickTodayCurrency`, so every card
 * on the strip is in the same money. `meId` resolves the `'me'` condition and
 * folds this device's unsent rows onto the signed-in person.
 */
export type BusinessContext = {
  readonly today: string;
  /** Null on a business with no money record. Money statements refuse to compile. */
  readonly currency: string | null;
  readonly meId: string | null;
  /**
   * WHICH business. Every statement here scans the mirror without naming a
   * record, so this is the one thing keeping "the whole business" from meaning
   * "every business on this phone".
   *
   * Required, and never nullable, since the switcher shipped: a phone holds
   * several businesses and a card that forgot which one it was about would add
   * up two shops. `useBusinessInsights` answers nothing at all until the
   * active business is known, so there is no caller left with a maybe.
   *
   * Making it required is what retired `entry_mirror_day`: no statement here
   * can be compiled without a tenant any more, so a day-only index had no
   * query left to serve and migration 7 drops it.
   */
  readonly tenantId: string;
};

/** Conditions in one order, with repeats dropped. Mirrors `sortFilters`. */
export const sortBusinessFilters = (
  filters: readonly BusinessFilter[] | undefined,
): readonly BusinessFilter[] | undefined => {
  if (filters === undefined || filters.length === 0) return undefined;
  const seen = new Set<string>();
  const kept: BusinessFilter[] = [];
  for (const given of filters) {
    // "Any of A, B" and "any of B, A" are one question, so a list is put in
    // one order, with repeats dropped, before it is keyed.
    const filter: BusinessFilter = isBusinessListFilter(given)
      ? { ...given, value: [...new Set(given.value)].sort() }
      : given;
    const key = `${filter.key}|${filter.op}|${businessValueKey(filter)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(filter);
  }
  kept.sort((a, b) => {
    if (a.key !== b.key) return a.key < b.key ? -1 : 1;
    if (a.op !== b.op) return a.op < b.op ? -1 : 1;
    const left = businessValueKey(a);
    const right = businessValueKey(b);
    return left < right ? -1 : left > right ? 1 : 0;
  });
  return kept;
};

/**
 * A combined card's tree with every leaf's conditions in one order, so two
 * ways of saying the same totals are the same card.
 */
export const sortBusinessTree = (node: BusinessAggNode): BusinessAggNode => {
  if (node.kind === 'agg') {
    const filters = sortBusinessFilters(node.filters);
    return filters === undefined
      ? { kind: 'agg', measure: node.measure }
      : { kind: 'agg', measure: node.measure, filters };
  }
  if (node.kind === 'calc') {
    return { kind: 'calc', op: node.op, left: sortBusinessTree(node.left), right: sortBusinessTree(node.right) };
  }
  if (node.kind === 'round') return { kind: 'round', of: sortBusinessTree(node.of), decimals: node.decimals };
  return node;
};

/* ------------------------------------------------------------- expressions */

const CALC_SIGNS: Readonly<Record<FormulaCalcOp, string>> = {
  add: '+',
  subtract: '-',
  multiply: '*',
  divide: '/',
};

const FROM = 'FROM entry_mirror e JOIN record_catalog r ON r.id = e.record_id';

/**
 * The same join, driven from the catalog: each of this business's records,
 * then that record's rows through an index that leads on `record_id`.
 *
 * Only the all-time statements use it, and only behind `UNFILED`. See
 * `allTimeStatement` for why the two joins are the same rows.
 */
const BY_RECORD = 'FROM record_catalog r JOIN entry_mirror e ON e.record_id = r.id';

/**
 * True when some live row, or some catalog record, is not filed under any
 * business.
 *
 * The one case where "this business's rows" and "the rows of this business's
 * records" can differ. A record id is a uuid that belongs to one business, and
 * every write path stamps a row with its record's business — so a row filed
 * under ANOTHER business than its record's cannot happen. A row filed under
 * none can: migration 6 leaves a device holding two cursors unlabelled rather
 * than guessing, and a later pull labels the catalog without re-filing the old
 * rows. On such a device the all-time statements take the old, slow join,
 * whose answer is the definition.
 *
 * `unlikely()` is what keeps this a seek. With `ANALYZE` statistics saying the
 * tenant column holds two values, the planner reckons `IS NULL` matches half
 * the mirror and scans it — seventy milliseconds to learn "no". Told the term
 * is rarely true, it seeks `entry_mirror_tenant_day` instead, and a missing
 * index degrades to that scan rather than to an error the way `INDEXED BY`
 * would.
 */
const UNFILED =
  'EXISTS (SELECT 1 FROM entry_mirror WHERE deleted_at IS NULL AND unlikely(tenant_id IS NULL)) ' +
  'OR EXISTS (SELECT 1 FROM record_catalog WHERE tenant_id IS NULL)';

/**
 * The money expression for one side of the flow.
 *
 * The negation for `out` is INSIDE the CASE, not around the aggregate. That is
 * what keeps `min`, `max` and `avg` honest: the largest outflow really is the
 * largest number this expression produces. Negating the aggregate instead would
 * turn `MAX` into the SMALLEST outflow, which is the same defect that had
 * "Top category" naming the cheapest one on a money-out record.
 *
 * `>= 0` counts a zero as in, which is `toTodayTotals`' rule, kept so home's
 * two tiles cannot drift from the cards that replace them.
 */
const flowExpr = (flow: BusinessFlow, linkRows: 'counted' | 'left_out' = 'counted'): string => {
  const side =
    flow === 'net'
      ? 'e.signed_value'
      : flow === 'in'
        ? 'CASE WHEN e.signed_value >= 0 THEN e.signed_value END'
        : 'CASE WHEN e.signed_value < 0 THEN -e.signed_value END';
  return linkRows === 'counted' ? side : `CASE WHEN ${LINK_POSTED} THEN NULL ELSE ${side} END`;
};

/**
 * A row a link posted: the copy of a sale that a feed link wrote into Cash.
 *
 * Money is counted once (Q5 of docs/plans/qa-fixes-1.md, F10 of the first QA
 * run). A total across records that added the sale AND its Cash copy said
 * "₹80 in today" for one ₹40 sale, so such a total leaves the copy out. It is
 * left out of the money expression rather than the WHERE, so a count still
 * counts every entry, the all-time money branch still collects its rowids off
 * the index alone, and an aggregate over no money reads NULL as it always has.
 * A literal and not a bound value: it is a word of the schema, like `'local'`.
 */
const LINK_POSTED = `IFNULL(e.source_channel, '') = 'link'`;

/**
 * Whether a money total keeps the rows a link posted.
 *
 * Kept when the conditions narrow the total to one record, because then it is
 * that record's own total, and a link-posted row counts in its target's totals
 * (Q6): "Money in, only Cash Register" is the cash that landed, links and all.
 * Kept too when a condition names a channel, because then the person decided
 * which channels count, and "only rows a link posted" must not read nought.
 * Everywhere else the total reaches several records and leaves them out.
 */
export const linkRowsFor = (filters: readonly BusinessFilter[]): 'counted' | 'left_out' => {
  const oneRecord = filters.some(
    (filter) => filter.key === 'record' && (filter.op === 'is' || (filter.op === 'in' && filter.value.length === 1)),
  );
  const channelNamed = filters.some((filter) => filter.key === 'channel');
  return oneRecord || channelNamed ? 'counted' : 'left_out';
};

/**
 * Who wrote an entry, with this device's unsent rows folded onto the person
 * signed in.
 *
 * `insertPending` writes `created_by = 'local'`, because the device mints the
 * row before any server has seen it. Left alone, a breakdown by writer would
 * show a phantom person called "local" that vanishes on the next sync. Binding
 * the signed-in id here makes the offline row belong to whoever typed it, which
 * is the truth.
 */
const creatorExpr = (): string => `CASE WHEN e.created_by = 'local' THEN ? ELSE e.created_by END`;

/** The value `creatorExpr` binds: the signed-in id, or a no-op when unknown. */
const creatorParam = (context: BusinessContext): string => context.meId ?? 'local';

const GROUP_COLUMN: Readonly<Record<Exclude<BusinessGroupKey, 'creator'>, string>> = {
  record: 'e.record_id',
  channel: 'e.source_channel',
  day: 'e.day',
};

const FILTER_COLUMN: Readonly<Record<Exclude<BusinessFilterKey, 'creator'>, string>> = {
  record: 'e.record_id',
  template: 'r.template_id',
  channel: 'e.source_channel',
};

/**
 * How a statement names the business.
 *
 *   e.tenant_id    the rows filed under it. Every windowed statement, because
 *                  `entry_mirror_tenant_day` makes the pair one seek.
 *   +e.tenant_id   the same test, with the unary plus telling SQLite not to
 *                  use an index for it. For "records gone quiet", which walks
 *                  one record's days newest first: offered the tenant index,
 *                  a phone with no `ANALYZE` statistics took it and walked the
 *                  whole business's days checking each row's record — eight
 *                  tenths of a second — instead of seeking the record's own.
 *   r.tenant_id    the business's records, for the all-time statements that
 *                  start from the catalog. See `allTimeStatement`.
 */
type TenantTerm = 'e.tenant_id' | '+e.tenant_id' | 'r.tenant_id';

/* ---------------------------------------------------------------- compiler */

/**
 * One spec, one statement, or null when the spec cannot be answered.
 *
 * Null has exactly one cause: a money measure on a business with no currency.
 * That is not an error — a shop keeping only a stock register has no rupee
 * total — and the card draws its quiet placeholder, the same as a record whose
 * view has not been built yet.
 */
export const compileBusinessInsight = (
  spec: BusinessOwnSpec,
  context: BusinessContext,
): CompiledInsight | null => {
  const money = isMoneyMeasure(spec.measure);
  if (money && context.currency === null) return null;
  const combined = spec.measure.agg === 'formula' ? spec.measure.expr : null;
  if (combined !== null) {
    const leaves = businessLeavesOf(combined);
    // The same frame budget a record's Combine keeps, and a Combine of nothing
    // is not a card.
    if (leaves.length === 0 || leaves.length > INSIGHT_AGG_MAX_LEAVES) return null;
  }
  // A combined card applies the currency per total: a count beside a sum is
  // not narrowed to the money's records, and the money is still in one currency.
  const outerCurrency = money && combined === null;
  // Money is counted once across records; one record's own total keeps its
  // link-posted rows. A combined card decides this per total, in `leafSql`.
  const linkRows = linkRowsFor(spec.filters ?? []);

  const params: (string | number)[] = [];
  const kind: BusinessKind = spec.kind ?? 'record';

  /**
   * One condition, as a predicate, binding its value in textual order.
   *
   * `is_not` is written `IFNULL(expr, '') <> ?` rather than `(expr IS NULL OR
   * expr <> ?)`, and that is not a style choice. The creator expression carries
   * a `?` of its own, so writing it twice would put two marks in the text for
   * one pushed value and SQLite would bind the wrong thing into the middle of a
   * CASE without complaining. One occurrence, one push, no counting. The
   * meaning is the one that was wanted either way: an entry whose writer is
   * unknown is not this writer, so it is kept — a value is never the empty
   * string, so nothing else changes.
   */
  const filterSql = (filter: BusinessFilter): string => {
    let column: string;
    if (filter.key === 'creator') {
      params.push(creatorParam(context));
      column = creatorExpr();
    } else {
      column = FILTER_COLUMN[filter.key];
    }
    // 'me' means whoever is signed in. With nobody signed in it can match
    // nothing but the local sentinel, which is the honest answer.
    const bound = (value: string): string =>
      filter.key === 'creator' && value === ME ? (context.meId ?? 'local') : value;
    if (isBusinessListFilter(filter)) {
      // A list of none would match nothing, or everything; the reader never
      // hands one over, and this refuses to invent either answer.
      const values = filter.value.length === 0 ? [''] : filter.value;
      params.push(...values.map(bound));
      const marks = values.map(() => '?').join(', ');
      // The same IFNULL as `is_not`, for the same reason: one mention of the
      // column, one push for its own `?`.
      return filter.op === 'in' ? `${column} IN (${marks})` : `IFNULL(${column}, '') NOT IN (${marks})`;
    }
    params.push(bound(filter.value));
    return filter.op === 'is' ? `${column} = ?` : `IFNULL(${column}, '') <> ?`;
  };

  /**
   * The whole WHERE, in the one order every statement uses: tombstones, the
   * business, the day window, the kind of record, the currency, then the spec's
   * own conditions. `params` is pushed in exactly that order, so the caller can
   * hand it straight to `getAllSync`.
   *
   * The business comes first among the bound predicates, and on the MIRROR
   * rather than on the joined catalog, because `entry_mirror_tenant_day` leads
   * on the tenant and then the day: the pair is one seek into one business's
   * days. Written against `r.tenant_id` instead, SQLite would have to read
   * every business's rows in the window and throw most of them away.
   */
  const where = (window: DayWindow | null, tenant: TenantTerm = 'e.tenant_id'): string => {
    const parts = ['e.deleted_at IS NULL'];
    params.push(context.tenantId);
    // The other two spellings belong to the statements that reach rows record
    // by record — see `TenantTerm`. Same position, same binding, so the order
    // below holds for all three.
    parts.push(`${tenant} = ?`);
    if (window !== null) {
      params.push(window.from, window.to);
      parts.push('e.day >= ?', 'e.day <= ?');
    }
    params.push(kind);
    parts.push('r.kind = ?');
    if (outerCurrency && context.currency !== null) {
      params.push(context.currency);
      parts.push('r.currency = ?');
    }
    for (const filter of spec.filters ?? []) parts.push(filterSql(filter));
    return `WHERE ${parts.join(' AND ')}`;
  };

  const windowOf = (scope: InsightScope): DayWindow | null =>
    scope === 'all' ? null : windowFor(scope, context.today);

  /**
   * One total of a combined card, as a conditional aggregate over the rows the
   * statement already scans: its window (for a pair's two halves), its
   * currency when it reads money, then its own conditions. Pushed in the order
   * the marks appear.
   *
   * A SUM or a count over no rows is nought, as a record's Combine writes it:
   * money in minus money out on a day with no expenses is the money in. An
   * average, a smallest and a largest keep their NULL.
   */
  const leafSql = (leaf: BusinessAggLeaf, window: DayWindow | null): string => {
    const parts: string[] = [];
    if (window !== null) {
      params.push(window.from, window.to);
      parts.push('e.day >= ?', 'e.day <= ?');
    }
    if (plainIsMoney(leaf.measure) && context.currency !== null) {
      params.push(context.currency);
      parts.push('r.currency = ?');
    }
    for (const filter of leaf.filters ?? []) parts.push(filterSql(filter));
    const when = parts.length === 0 ? '1' : parts.join(' AND ');
    if (leaf.measure.agg === 'rows') return `COUNT(CASE WHEN ${when} THEN 1 END)`;
    const value = flowExpr(leaf.measure.flow, linkRowsFor([...(spec.filters ?? []), ...(leaf.filters ?? [])]));
    const fn = leaf.measure.agg.toUpperCase();
    return leaf.measure.agg === 'sum'
      ? `IFNULL(SUM(CASE WHEN ${when} THEN ${value} END), 0)`
      : `${fn}(CASE WHEN ${when} THEN ${value} END)`;
  };

  /** The combined tree as one expression: `const`, `calc` and `round` as a record's. */
  const treeSql = (node: BusinessAggNode, window: DayWindow | null): string => {
    const part = (kid: BusinessAggNode): string => treeSql(kid, window);
    if (node.kind === 'agg') return leafSql(node, window);
    if (node.kind === 'const') {
      params.push(node.value);
      return '?';
    }
    if (node.kind === 'round') return `ROUND(${part(node.of)}, ${Math.trunc(node.decimals)})`;
    if (node.op === 'divide') {
      // Named three times in the text, so built three times: each build pushes
      // its own values in the order its marks appear.
      const zero = part(node.right);
      const left = part(node.left);
      const right = part(node.right);
      return `CASE WHEN IFNULL(${zero}, 0) = 0 THEN NULL ELSE (${left}) * 1.0 / (${right}) END`;
    }
    const left = part(node.left);
    const right = part(node.right);
    return `(${left} ${CALC_SIGNS[node.op]} ${right})`;
  };

  /** The aggregate, and the expression it reads. */
  const aggregate = (): string => {
    if (combined !== null) return treeSql(combined, null);
    if (spec.measure.agg === 'rows') return 'COUNT(1)';
    // idle_days compiles its own statement and never reaches here. Counting is
    // the harmless answer if a future shape ever forgets to route it.
    if (spec.measure.agg === 'idle_days' || spec.measure.agg === 'formula') return 'COUNT(1)';
    return `${spec.measure.agg.toUpperCase()}(${flowExpr(spec.measure.flow, linkRows)})`;
  };

  /**
   * The grouped expression, pushing the one parameter it may need.
   *
   * Called ONCE per statement, and the statement interpolates it once: the
   * GROUP BY, the HAVING and the ORDER BY all reach it through the `"key"`
   * alias instead of repeating the expression. Repeating it would put three
   * `?` marks in the text for one pushed value, and SQLite would bind the
   * window into the middle of a CASE without complaining.
   */
  const groupExpr = (key: BusinessGroupKey): string => {
    if (key !== 'creator') return GROUP_COLUMN[key];
    params.push(creatorParam(context));
    return creatorExpr();
  };

  /**
   * How a ranking orders itself.
   *
   * By SIZE for money, because a "top" list is about magnitude and the sign
   * already says the direction — the rule `insight-query.ts` had to learn when
   * a money-out record ranked its cheapest category first. Idleness is already
   * unsigned, so it ranks plainly. A day ties on the most recent.
   */
  const rank = (key: BusinessGroupKey | null): string => {
    // The record id breaks a tie so equally quiet records keep one order. The
    // old grouped statement named none, and on a phone (no statistics) its
    // sorter handed ties back in DESCENDING record id; this says so out loud,
    // so the card lists them in the order it always has.
    if (spec.measure.agg === 'idle_days') return '"value" DESC, "key" DESC';
    if (key === 'day') return '"value" DESC, "key" DESC';
    return 'ABS("value") DESC';
  };

  /**
   * Days since each record was last written to.
   *
   * Its own statement, because it reads the latest day rather than aggregating
   * a value, ignores the window entirely, and needs no currency. `julianday`
   * gives whole days between two YYYY-MM-DD strings.
   *
   * **Asked record by record, not grouped.** `MAX(e.day) … GROUP BY record`
   * walks every row of the business and reads each one's `tenant_id` off the
   * table — half a second on five lakh rows, for a handful of answers. Asked
   * per catalog record as "the newest day, newest first, one row", each answer
   * is a seek to the end of that record's range in `entry_mirror_agg` and one
   * row read to check the business. The rows are the same rows: the subquery
   * keeps every predicate the grouped form had, `e.tenant_id` included, and a
   * record none of whose rows qualify answers NULL and is dropped, exactly as
   * it formed no group before. NULL days sort last in a descending walk, and
   * `e.day IS NOT NULL` skips them anyway, so the newest day is MAX's answer.
   *
   * The latest day is taken in an inner SELECT and filtered there, so a stored
   * day `julianday` cannot read still ranks as a NULL rather than vanishing.
   */
  const idleStatement = (limit: number): CompiledInsight => {
    params.push(context.today);
    const clause = where(null, '+e.tenant_id');
    params.push(limit);
    return {
      sql:
        `SELECT "key", CAST(julianday(?) - julianday("last") AS INTEGER) AS "value" FROM (` +
        `SELECT r.id AS "key", (SELECT e.day FROM entry_mirror e ${clause} ` +
        `AND e.record_id = r.id AND e.day IS NOT NULL ORDER BY e.day DESC LIMIT 1) AS "last" ` +
        `FROM record_catalog r) WHERE "last" IS NOT NULL ` +
        `ORDER BY ${rank('record')} LIMIT ?`,
      params,
    };
  };

  /**
   * One number over all of time: a count, or a money aggregate.
   *
   * The windowed form seeks `entry_mirror_tenant_day` on a range of days. With
   * no range, the same form reads every row of the business off the table in
   * index order — a random read per row — for a check the index could not
   * answer, and on five lakh rows that was a quarter to a third of a second.
   *
   * **The same rows, found from the other end.** "Rows filed under this
   * business" and "rows of this business's records" are one set everywhere
   * `UNFILED` is false — see it for why — so the fast branch starts from the
   * catalog, `r.tenant_id = ?`, and reaches each record's rows through an index
   * that leads on `record_id`:
   *
   *   count  never touches the table: `entry_mirror_record_live` (and the
   *          partial `deleted_at IS NULL` it is built on) answers it alone.
   *   money  must read `signed_value`, which no index holds, and adding one
   *          would cost tens of megabytes. So it collects the matching rowids
   *          first — from the index, cheap — and SQLite sorts them for the
   *          `IN`, so the table is then read in its own order instead of at
   *          random. Same rows, same arithmetic, fewer than half the pages
   *          touched out of order.
   *
   * The old statement stays inside the CASE as the answer where `UNFILED` is
   * true. SQLite runs a scalar subquery only when the CASE reaches it, so on
   * every ordinary device that branch costs nothing.
   *
   * Parameters are pushed in textual order: the fallback's clause, then the
   * fast branch's, and the conditions bind once in each.
   */
  const allTimeStatement = (): CompiledInsight => {
    const exact = `SELECT ${aggregate()} ${FROM} ${where(null)}`;
    const fast =
      spec.measure.agg === 'rows'
        ? `SELECT COUNT(1) ${BY_RECORD} ${where(null, 'r.tenant_id')}`
        : combined !== null
          ? // A combined card's totals read the catalog's currency per row,
            // so its fast branch keeps the join rather than the rowid IN.
            `SELECT ${aggregate()} ${BY_RECORD} ${where(null, 'r.tenant_id')}`
          : // The inner `e` shadows the outer one, so the conditions inside the
            // IN read the rows they filter, and the aggregate outside reads the
            // rows they picked.
            `SELECT ${aggregate()} FROM entry_mirror e WHERE e.rowid IN ` +
            `(SELECT e.rowid ${BY_RECORD} ${where(null, 'r.tenant_id')})`;
    return {
      sql: `SELECT CASE WHEN ${UNFILED} THEN (${exact}) ELSE (${fast}) END AS "value"`,
      params,
    };
  };

  switch (spec.shape) {
    case 'scalar': {
      if (spec.measure.agg === 'idle_days') return idleStatement(1);
      if (spec.leaderOf === undefined) {
        if (spec.scope === 'all') return allTimeStatement();
        return { sql: `SELECT ${aggregate()} AS "value" ${FROM} ${where(windowOf(spec.scope))}`, params };
      }
      // The key expression is written twice — in the SELECT and in the GROUP BY
      // — so its parameter, if it has one, is pushed twice too. Building it
      // once and reusing the string would bind one value for two marks.
      const key = groupExpr(spec.leaderOf);
      // The value before the clause: a combined card's totals bind values of
      // their own, and they appear in the text before the WHERE does.
      const value = aggregate();
      const clause = where(windowOf(spec.scope));
      return {
        sql:
          `SELECT ${key} AS "key", ${value} AS "value" ${FROM} ` +
          `${clause} GROUP BY "key" HAVING "key" IS NOT NULL ` +
          `ORDER BY ${rank(spec.leaderOf)} LIMIT 1`,
        params,
      };
    }

    case 'scalar_pair': {
      // Idleness is a distance from today, so it has no period before it to be
      // compared against. Refusing beats inventing a second meaning.
      if (spec.measure.agg === 'idle_days') return null;
      const current = windowFor(spec.scope, context.today);
      const previous = previousWindowFor(spec.scope, context.today);
      if (spec.measure.agg === 'formula') {
        // Each total appears once per window, and is built once per window.
        const now = treeSql(spec.measure.expr, current);
        const before = treeSql(spec.measure.expr, previous);
        const clause = where({ from: previous.from, to: current.to });
        return { sql: `SELECT ${now} AS "value", ${before} AS "against" ${FROM} ${clause}`, params };
      }
      const inner = spec.measure.agg === 'rows' ? '1' : flowExpr(spec.measure.flow, linkRows);
      const fn = spec.measure.agg === 'rows' ? 'COUNT' : spec.measure.agg.toUpperCase();
      const slice = (window: DayWindow): string => {
        params.push(window.from, window.to);
        return `${fn}(CASE WHEN e.day >= ? AND e.day <= ? THEN ${inner} END)`;
      };
      const value = slice(current);
      const against = slice(previous);
      // The outer window spans both slices, so the scan stays on the day index
      // and the conditions apply to each side identically.
      const clause = where({ from: previous.from, to: current.to });
      return {
        sql: `SELECT ${value} AS "value", ${against} AS "against" ${FROM} ${clause}`,
        params,
      };
    }

    case 'series': {
      const window = seriesWindow(spec.days, context.today);
      return {
        sql:
          `SELECT e.day AS "day", ${aggregate()} AS "value" ${FROM} ${where(window)} ` +
          `GROUP BY e.day ORDER BY e.day ASC`,
        params,
      };
    }

    case 'breakdown': {
      if (spec.measure.agg === 'idle_days') return idleStatement(GROUP_SCAN_LIMIT);
      const key = groupExpr(spec.by);
      const value = aggregate();
      const clause = where(windowOf(spec.scope));
      params.push(GROUP_SCAN_LIMIT);
      return {
        sql:
          `SELECT ${key} AS "key", ${value} AS "value" ${FROM} ` +
          `${clause} GROUP BY "key" HAVING "key" IS NOT NULL ` +
          `ORDER BY ${rank(spec.by)} LIMIT ?`,
        params,
      };
    }
  }
};

/* ------------------------------------------------------------- the horizon */

/**
 * The latest day any record in this business has been pruned to, or null when
 * every record still holds everything.
 *
 * ONE day for the whole business, and it is the LATEST of the per-record
 * floors, because a business-wide statement scans every record at once: if any
 * one of them is missing rows for a window, the sum over all of them is short
 * by that record and there is no way to tell from the answer.
 */
export type BusinessHorizon = { readonly floorDay: string | null };

/** Which of the bucket's four numbers this measure reads, or null for one no bucket holds. */
const bucketColumn = (measure: BusinessMeasure): 'value' | 'rows' | 'money_in' | 'money_out' | null => {
  if (measure.agg === 'rows') return 'rows';
  if (measure.agg !== 'sum') return null;
  return measure.flow === 'net' ? 'value' : measure.flow === 'in' ? 'money_in' : 'money_out';
};

/**
 * A business card answered from the buckets rather than from the rows.
 *
 * Null means no bucket answers it, and the card draws the quiet placeholder.
 * Every refusal is a dimension a bucket does not have: a condition, a group by
 * the person who wrote a row or the channel it came through, an average, a
 * smallest or a largest. Summed daily totals cannot give any of those back, and
 * a number that pretends otherwise is the failure this design exists to stop.
 *
 * What it CAN answer is what the Dashboard mostly asks: how much came in, how
 * much went out, the net, how many entries, each over any window, per day or
 * per record. Money is counted once (`businessMoneyAtHorizon`); a count reads
 * the buckets whole.
 */
const businessBucketInsight = (
  spec: BusinessOwnSpec,
  context: BusinessContext,
): CompiledInsight | null => {
  if ((spec.filters ?? []).length > 0) return null;
  if (spec.shape === 'scalar' && spec.leaderOf !== undefined) return null;
  if (spec.shape === 'breakdown' && spec.by !== 'record' && spec.by !== 'day') return null;
  const column = bucketColumn(spec.measure);
  if (column === null) return null;
  const money = isMoneyMeasure(spec.measure);
  if (money && context.currency === null) return null;
  const kind: BusinessKind = spec.kind ?? 'record';
  if (money && spec.measure.agg === 'sum') return businessMoneyAtHorizon(spec, context, column, spec.measure.flow, kind);

  const params: (string | number)[] = [];

  /** The banked half: what the server has already summed for each day. */
  const banked = (window: DayWindow | null, select: string, group: string): string => {
    const parts = ['b.tenant_id = ?'];
    params.push(context.tenantId);
    if (window !== null) {
      params.push(window.from, window.to);
      parts.push('b.day >= ?', 'b.day <= ?');
    }
    params.push(kind);
    parts.push('r.kind = ?');
    if (money && context.currency !== null) {
      params.push(context.currency);
      parts.push('r.currency = ?');
    }
    return (
      `SELECT ${select} FROM aggregate_bucket b JOIN record_catalog r ON r.id = b.record_id ` +
      `WHERE ${parts.join(' AND ')}${group}`
    );
  };

  /**
   * The unbanked half: rows this device has written and the server has not seen.
   *
   * A bucket holds only what was accepted, so an entry saved offline is in the
   * mirror and in no bucket. Adding the pending rows of the same window back on
   * is what keeps the two halves one answer, and a row that syncs simply moves
   * from one half to the other.
   */
  const pendingExpr =
    spec.measure.agg === 'rows'
      ? 'COUNT(1)'
      : `SUM(${flowExpr((spec.measure as { flow: BusinessFlow }).flow)})`;

  /**
   * Rows the server has never seen, which is what the buckets are missing.
   *
   * `server_seq IS NULL` and not merely `pending = 1`: a row that was pushed
   * and then edited offline is in both halves — its old amount in the bucket,
   * its new amount here — and adding it would count it twice. A pending edit
   * therefore leaves the card reading what the server last accepted, until the
   * push lands.
   */
  const unbanked = (window: DayWindow | null, select: string, group: string): string => {
    const parts = ['e.deleted_at IS NULL', 'e.pending = 1', 'e.server_seq IS NULL', 'e.tenant_id = ?'];
    params.push(context.tenantId);
    if (window !== null) {
      params.push(window.from, window.to);
      parts.push('e.day >= ?', 'e.day <= ?');
    }
    params.push(kind);
    parts.push('r.kind = ?');
    if (money && context.currency !== null) {
      params.push(context.currency);
      parts.push('r.currency = ?');
    }
    return (
      `SELECT ${select} FROM entry_mirror e JOIN record_catalog r ON r.id = e.record_id ` +
      `WHERE ${parts.join(' AND ')}${group}`
    );
  };

  const total = (window: DayWindow | null): string =>
    `(${banked(window, `COALESCE(SUM(b.${column}), 0)`, '')}) + ` +
    `(${unbanked(window, `COALESCE(${pendingExpr}, 0)`, '')})`;

  /**
   * The guard that turns "this business has no buckets" into no answer at all.
   *
   * A sum over an empty table is nought, and nought under a confident label is
   * the failure this design exists to prevent. A business whose first download
   * has not finished, or whose records the server withheld the buckets for,
   * draws the quiet placeholder instead.
   */
  const hasBuckets = (): string => {
    params.push(context.tenantId);
    return HAS_BUCKETS;
  };

  switch (spec.shape) {
    case 'scalar': {
      const one = total(spec.scope === 'all' ? null : windowFor(spec.scope, context.today));
      return { sql: `SELECT ${one} AS "value" WHERE ${hasBuckets()}`, params };
    }
    case 'scalar_pair': {
      const value = total(windowFor(spec.scope, context.today));
      const against = total(previousWindowFor(spec.scope, context.today));
      return { sql: `SELECT ${value} AS "value", ${against} AS "against" WHERE ${hasBuckets()}`, params };
    }
    case 'series': {
      const window = seriesWindow(spec.days, context.today);
      const first = banked(window, `b.day AS "day", b.${column} AS "value"`, '');
      const second = unbanked(window, `e.day AS "day", ${flowOrOne(spec.measure)} AS "value"`, '');
      return {
        sql:
          `SELECT "day", SUM("value") AS "value" FROM (${first} UNION ALL ${second}) ` +
          `WHERE ${hasBuckets()} GROUP BY "day" ORDER BY "day" ASC`,
        params,
      };
    }
    case 'breakdown': {
      const window = spec.scope === 'all' ? null : windowFor(spec.scope, context.today);
      const key = spec.by === 'day' ? 'day' : 'record_id';
      // The two halves are built BEFORE the limit is pushed, because they push
      // their own values as they are built and the limit's `?` is the last mark
      // in the text. Pushed first, it would bind into the tenant and leave the
      // limit reading a uuid, which SQLite takes as nought — an empty card.
      const first = banked(window, `b.${key} AS "key", b.${column} AS "value"`, '');
      const second = unbanked(window, `e.${key} AS "key", ${flowOrOne(spec.measure)} AS "value"`, '');
      const guard = hasBuckets();
      params.push(spec.limit);
      return {
        sql:
          `SELECT "key", SUM("value") AS "value" FROM (${first} UNION ALL ${second}) ` +
          `WHERE "key" IS NOT NULL AND ${guard} GROUP BY "key" ORDER BY ABS(SUM("value")) DESC LIMIT ?`,
        params,
      };
    }
  }
};

/** The guard that turns "this business has no buckets" into no answer at all; it binds the tenant. */
const HAS_BUCKETS = 'EXISTS (SELECT 1 FROM aggregate_bucket WHERE tenant_id = ?)';

/**
 * The records a link posts into, as the mirror shows them: those holding a row
 * a link posted. It binds the tenant and the channel, and not a literal, because
 * no bucket statement carries a quote. A row a link posted keeps its
 * `source_channel` once it syncs, and pruning keeps a record's newest rows, so a
 * link in use is always seen here; one whose copies were all pruned away (a
 * link removed long ago) is not.
 */
const LINK_TARGETS = 'SELECT record_id FROM entry_mirror WHERE tenant_id = ? AND deleted_at IS NULL AND source_channel = ?';

/**
 * A money card below the floor, counted once (Q5 of docs/plans/qa-fixes-1.md).
 *
 * Money is counted once across records, and a link-posted row counts only in
 * its target's own totals (Q6). A bucket is a whole record-day summed, so
 * Cash's bucket holds the copy a feed link posted of a sale, and nothing in it
 * tells that copy from a row typed into Cash. So each record is read where it
 * can be read exactly:
 *
 *   from its own floor on   its rows, which the device holds whole there, the
 *   (or all of it, with     copies left out as the row path leaves them out;
 *   no floor)
 *   below its floor         its buckets, and the rows the server has not seen,
 *                           for a record no link posts into: its buckets hold
 *                           no copy;
 *   below its floor, a      nothing can say it: a card whose window holds such
 *   link's target           a bucketed day draws the quiet placeholder.
 *
 * So a card the rows (and copy-free buckets) answer always answers, on the
 * phone and on the web, whose mirror prunes the same way (18 Oct review); only
 * a day a link's target no longer holds is left unsaid. A business with no
 * links reads every record so, with no day unsaid.
 */
const businessMoneyAtHorizon = (
  spec: BusinessOwnSpec,
  context: BusinessContext,
  column: 'value' | 'money_in' | 'money_out' | 'rows',
  flow: BusinessFlow,
  kind: BusinessKind,
): CompiledInsight | null => {
  const params: (string | number)[] = [];
  const side =
    flow === 'net'
      ? 'e.signed_value'
      : flow === 'in'
        ? 'CASE WHEN e.signed_value >= 0 THEN e.signed_value END'
        : 'CASE WHEN e.signed_value < 0 THEN -e.signed_value END';
  /** One row's money with a link's copy left out (`flowExpr`'s `left_out`), its words bound. */
  const rowMoney = (): string => {
    params.push('', 'link');
    return `CASE WHEN IFNULL(e.source_channel, ?) = ? THEN NULL ELSE ${side} END`;
  };
  /** The window, the kind and the currency, on the table `t` stands for, binding in the order written. */
  const scoped = (t: 'b' | 'e', window: DayWindow | null): string[] => {
    const parts: string[] = [];
    if (window !== null) {
      params.push(window.from, window.to);
      parts.push(`${t}.day >= ?`, `${t}.day <= ?`);
    }
    params.push(kind);
    parts.push('r.kind = ?');
    if (context.currency !== null) {
      params.push(context.currency);
      parts.push('r.currency = ?');
    }
    return parts;
  };
  const targets = (): string => {
    params.push(context.tenantId, 'link');
    return LINK_TARGETS;
  };

  /** The rows a record holds whole: from its floor on, or all of them with none. A row with no day belongs to no bucket and is never pruned. */
  const held = (window: DayWindow | null, select: () => string): string => {
    const picked = select();
    params.push(context.tenantId);
    const parts = ['e.deleted_at IS NULL', 'e.tenant_id = ?', ...scoped('e', window), '(r.floor_day IS NULL OR e.day IS NULL OR e.day >= r.floor_day)'];
    return `SELECT ${picked} FROM entry_mirror e JOIN record_catalog r ON r.id = e.record_id WHERE ${parts.join(' AND ')}`;
  };
  /** Below its floor, a record no link posts into: what the server banked for each day. */
  const banked = (window: DayWindow | null, select: string): string => {
    params.push(context.tenantId);
    const parts = ['b.tenant_id = ?', ...scoped('b', window), 'b.day < r.floor_day', `b.record_id NOT IN (${targets()})`];
    return `SELECT ${select} FROM aggregate_bucket b JOIN record_catalog r ON r.id = b.record_id WHERE ${parts.join(' AND ')}`;
  };
  /** Below its floor, the same records' rows the server has never seen, which no bucket holds (`unbanked`). */
  const unbanked = (window: DayWindow | null, select: () => string): string => {
    const picked = select();
    params.push(context.tenantId);
    const parts = ['e.deleted_at IS NULL', 'e.pending = 1', 'e.server_seq IS NULL', 'e.tenant_id = ?', ...scoped('e', window), 'e.day < r.floor_day', `e.record_id NOT IN (${targets()})`];
    return `SELECT ${picked} FROM entry_mirror e JOIN record_catalog r ON r.id = e.record_id WHERE ${parts.join(' AND ')}`;
  };
  /** Whether the window holds a banked day below the floor of a record a link posts into: what no number here can say. */
  const unsaid = (window: DayWindow | null): string => {
    params.push(context.tenantId);
    const parts = ['b.tenant_id = ?', ...scoped('b', window), 'b.day < r.floor_day', `b.record_id IN (${targets()})`];
    return `EXISTS (SELECT 1 FROM aggregate_bucket b JOIN record_catalog r ON r.id = b.record_id WHERE ${parts.join(' AND ')})`;
  };
  const guard = (...windows: (DayWindow | null)[]): string => {
    params.push(context.tenantId);
    return [HAS_BUCKETS, ...windows.map((window) => `NOT ${unsaid(window)}`)].join(' AND ');
  };

  const total = (window: DayWindow | null): string =>
    `(${held(window, () => `COALESCE(SUM(${rowMoney()}), 0)`)}) + ` +
    `(${banked(window, `COALESCE(SUM(b.${column}), 0)`)}) + ` +
    `(${unbanked(window, () => `COALESCE(SUM(${rowMoney()}), 0)`)})`;
  const perKey = (window: DayWindow | null, key: 'day' | 'record_id'): string =>
    `${held(window, () => `e.${key} AS "key", ${rowMoney()} AS "value"`)} UNION ALL ` +
    `${banked(window, `b.${key} AS "key", b.${column} AS "value"`)} UNION ALL ` +
    `${unbanked(window, () => `e.${key} AS "key", ${rowMoney()} AS "value"`)}`;

  switch (spec.shape) {
    case 'scalar': {
      const window = spec.scope === 'all' ? null : windowFor(spec.scope, context.today);
      const one = total(window);
      return { sql: `SELECT ${one} AS "value" WHERE ${guard(window)}`, params };
    }
    case 'scalar_pair': {
      const now = windowFor(spec.scope, context.today);
      const before = previousWindowFor(spec.scope, context.today);
      const value = total(now);
      const against = total(before);
      return { sql: `SELECT ${value} AS "value", ${against} AS "against" WHERE ${guard(now, before)}`, params };
    }
    case 'series': {
      const window = seriesWindow(spec.days, context.today);
      const halves = perKey(window, 'day');
      return {
        sql: `SELECT "key" AS "day", SUM("value") AS "value" FROM (${halves}) WHERE "key" IS NOT NULL AND ${guard(window)} GROUP BY "key" ORDER BY "key" ASC`,
        params,
      };
    }
    case 'breakdown': {
      const window = spec.scope === 'all' ? null : windowFor(spec.scope, context.today);
      const halves = perKey(window, spec.by === 'day' ? 'day' : 'record_id');
      // The guard and the halves push their values as they are built, and the limit's `?` is the last mark in the text.
      const guarded = guard(window);
      params.push(spec.limit);
      return {
        sql:
          `SELECT "key", SUM("value") AS "value" FROM (${halves}) ` +
          `WHERE "key" IS NOT NULL AND ${guarded} GROUP BY "key" ORDER BY ABS(SUM("value")) DESC LIMIT ?`,
        params,
      };
    }
  }
};

/** One pending row's contribution, per row rather than summed. */
const flowOrOne = (measure: BusinessMeasure): string =>
  measure.agg === 'rows' ? '1' : flowExpr((measure as { flow: BusinessFlow }).flow);

/**
 * `compileBusinessInsight`, with the pruned mirror taken into account.
 *
 * `idle_days` deliberately never takes the bucket path. It asks how long since
 * a record was last written to, and the last row of a record is the newest one
 * there is — which is exactly the row a window keeps. Pruning cannot move that
 * answer, so the mirror still owns it.
 */
export const compileBusinessInsightAtHorizon = (
  spec: BusinessOwnSpec,
  context: BusinessContext,
  horizon: BusinessHorizon,
): CompiledInsight | null => {
  const floor = horizon.floorDay;
  if (floor === null || spec.measure.agg === 'idle_days') return compileBusinessInsight(spec, context);
  const earliest =
    spec.shape === 'series'
      ? seriesWindow(spec.days, context.today).from
      : spec.shape === 'scalar_pair'
        ? previousWindowFor(spec.scope, context.today).from
        : spec.scope === 'all'
          ? null
          : windowFor(spec.scope, context.today).from;
  if (earliest !== null && earliest >= floor) return compileBusinessInsight(spec, context);
  return businessBucketInsight(spec, context);
};
